import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import _ from "lodash"
import Input from "components/forms/input"
import SelectSearchable from "components/forms/select-searchable"
import ErrorMessage from "components/forms/error-message"
import { Plus } from "components/icons/outline"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// Key lokal per baris item — item manual tidak punya product_unit_id, jadi butuh
// id lokal yang stabil untuk React key & update/hapus baris.
let __sjuUid = 0
const nextUid = () => `it_${++__sjuUid}`

// DECIMAL(18,3) dari backend ("1.000") → tampil "1", "2.500" → "2,5"
function formatQty(v) {
  return num(v).toLocaleString("id-ID", { maximumFractionDigits: 3 })
}

function formatMoney(v) {
  return "Rp " + num(v).toLocaleString("id-ID", { maximumFractionDigits: 0 })
}

function pickLabel(p) {
  return (
    p?.product_unit_long_name ||
    p?.product_unit ||
    p?.product_unit_name ||
    p?.sku ||
    p?.product_model ||
    p?.product_unit_id ||
    "-"
  )
}

const DIRECTIONS = [
  { value: "OUT", label: "KELUAR" },
  { value: "IN", label: "MASUK" },
]

export default function Form({ data, errors = {}, onSubmit, submitting }) {
  const { currentBranch } = useAuth()
  const navigate = useNavigate()

  const isEdit = Boolean(data?.id)

  const [docNumber, setDocNumber] = useState(data?.doc_number ?? "")
  const [docDate, setDocDate] = useState(data?.doc_date ?? todayISO())
  const [direction, setDirection] = useState((data?.direction ?? "OUT").toUpperCase() === "IN" ? "IN" : "OUT")
  const [fromParty, setFromParty] = useState(data?.from_party ?? "")
  const [toParty, setToParty] = useState(data?.to_party ?? "")
  const [notes, setNotes] = useState(data?.notes ?? "")

  const [items, setItems] = useState(() =>
    Array.isArray(data?.items)
      ? data.items.map((r) => ({
          _uid: nextUid(),
          product_unit_id: r.product_unit_id ?? null,
          product_unit_name: r.product_label ?? r.product_unit_name ?? r.product_unit_id ?? "",
          qty: r.qty != null ? num(r.qty) : 1,
          uom: r.uom ?? "PCS",
          // 200726: harga satuan opsional utk print faktur (kosong = "-" di faktur).
          price: r.price != null ? num(r.price) : "",
          notes: r.notes ?? "",
        }))
      : []
  )

  // Product picker (ps_list). Outlet hanya untuk konteks pencarian nama produk
  // — dokumen ini tidak menyentuh stok outlet manapun.
  const outletId = currentBranch?.id ?? null
  const [selectedId, setSelectedId] = useState("")
  const [selectedRow, setSelectedRow] = useState(null)
  const [addQty, setAddQty] = useState(1)
  const [productPage, setProductPage] = useState(1)
  const [productSearchValue, setProductSearchValue] = useState("")
  const [productSearch, setProductSearch] = useState("")
  const productCacheRef = useRef(new Map())

  const debouncedSetSearch = useMemo(
    () =>
      _.debounce((val) => {
        setProductSearch(val)
        setProductPage(1)
      }, 350),
    []
  )

  const onProductSearchChange = (val) => {
    setProductSearchValue(val)
    debouncedSetSearch(val)
  }

  const psListParams = useMemo(
    () => ({
      outlet_id: outletId ?? undefined,
      paginate: true,
      page: productPage,
      per_page: 20,
      search: productSearch,
    }),
    [outletId, productSearch, productPage]
  )

  const { data: productSelection, isLoading: isLoadingProducts } = useResultSet(
    process.env.REACT_APP_INVENTORY_API_URL,
    "ps_list",
    psListParams
  )

  const productRows = useMemo(
    () => (Array.isArray(productSelection?.data) ? productSelection.data : []),
    [productSelection]
  )

  useEffect(() => {
    for (const p of productRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) productCacheRef.current.set(key, p)
    }
  }, [productRows])

  const totalQty = useMemo(() => items.reduce((acc, r) => acc + num(r.qty), 0), [items])
  // 210726: total keseluruhan (Σ qty × harga) untuk footer tabel item.
  const grandTotal = useMemo(
    () => items.reduce((acc, r) => acc + num(r.qty) * num(r.price), 0),
    [items]
  )

  // 210726: tambah baris barang MANUAL (nama diketik bebas, tanpa katalog).
  const onAddManualItem = () =>
    setItems((prev) => [
      ...prev,
      { _uid: nextUid(), product_unit_id: null, product_unit_name: "", qty: 1, uom: "PCS", price: "", notes: "" },
    ])

  const onAddItem = () => {
    const pid = String(selectedId ?? "")
    if (!pid) {
      toast.error("Pilih produk dulu")
      return
    }
    const p = selectedRow || productCacheRef.current.get(pid) || productRows.find((x) => String(x?.product_unit_id) === pid)
    if (!p) {
      toast.error("Produk tidak ditemukan, pilih ulang.")
      return
    }

    const qty = num(addQty)
    if (qty <= 0) {
      toast.error("Qty harus lebih dari 0")
      return
    }

    setItems((prev) => {
      const idx = prev.findIndex((x) => String(x.product_unit_id) === pid)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = { ...next[idx], qty: num(next[idx].qty) + qty }
        return next
      }
      return [
        ...prev,
        {
          _uid: nextUid(),
          product_unit_id: pid,
          product_unit_name: pickLabel(p),
          qty,
          uom: p?.uom || "PCS",
          price: "",
          notes: "",
        },
      ]
    })

    setSelectedId("")
    setSelectedRow(null)
    setAddQty(1)
    setProductSearchValue("")
    setProductSearch("")
  }

  // 210726: update/hapus baris berbasis _uid (item manual tak punya product_unit_id).
  const onUpdateItem = (uid, patch) =>
    setItems((prev) => prev.map((it) => (it._uid === uid ? { ...it, ...patch } : it)))

  const onRemoveItem = (uid) => setItems((prev) => prev.filter((it) => it._uid !== uid))

  const buildPayload = () => ({
    doc_number: (docNumber || "").trim() || null,
    doc_date: docDate,
    direction,
    from_party: (fromParty || "").trim() || null,
    to_party: (toParty || "").trim() || null,
    notes: (notes || "").trim() || null,
    items: items
      // Buang baris benar-benar kosong (tanpa produk & tanpa nama).
      .filter((it) => it.product_unit_id || (it.product_unit_name || "").trim())
      .map((it) => ({
        product_unit_id: it.product_unit_id || null,
        product_unit_name: (it.product_unit_name || "").trim() || null,
        qty: num(it.qty),
        uom: it.uom || "PCS",
        price: it.price === "" || it.price == null ? null : num(it.price),
        notes: (it.notes || "").trim() || null,
      })),
  })

  const handleSubmit = () => {
    // Barang cukup ditulis di Catatan (permintaan user 170726 #5) — dokumen valid
    // selama Catatan terisi ATAU (dokumen lama) masih membawa item.
    if (items.length === 0 && !(notes || "").trim()) {
      toast.error("Isi Catatan / Daftar Barang dulu")
      return
    }
    onSubmit?.(buildPayload())
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 space-y-4 bg-white border rounded-2xl border-neutral-200">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs text-neutral-700">No. Surat Jalan</label>
            <Input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="Kosongkan untuk nomor otomatis"
            />
            <ErrorMessage error={errors?.doc_number} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Tanggal</label>
            <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
            <ErrorMessage error={errors?.doc_date} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Arah</label>
            <div className="inline-flex mt-1 overflow-hidden border rounded-xl border-neutral-200">
              {DIRECTIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDirection(d.value)}
                  className={`px-4 py-2 text-sm font-semibold ${
                    direction === d.value ? "bg-brand-900 text-white" : "bg-white text-neutral-700"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <ErrorMessage error={errors?.direction} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Dari (pengirim)</label>
            <Input value={fromParty} onChange={(e) => setFromParty(e.target.value)} placeholder="mis. Gudang HO" />
            <ErrorMessage error={errors?.from_party} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Kepada (penerima)</label>
            <Input value={toParty} onChange={(e) => setToParty(e.target.value)} placeholder="mis. Toko Cabang A" />
            <ErrorMessage error={errors?.to_party} />
          </div>

          <div className="md:col-span-3">
            {/* Permintaan user 170726 (#5): barang tidak perlu dipilih dari katalog —
                cukup DITULIS di catatan. Catatan jadi isi utama dokumen. */}
            <label className="block text-xs text-neutral-700">Catatan / Daftar Barang</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={"Tulis daftar barang & keterangan di sini, mis.:\n- Etalase kaca 2 unit\n- Kursi optik 1 unit"}
              rows={6}
              className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>
        </div>
      </div>

      {/* 210726: input barang MANUAL (nama diketik, harga, satuan, total). Barang
          juga masih boleh cukup ditulis di Catatan di atas. */}
      <div className="p-4 space-y-4 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Daftar Barang</h3>
            <p className="text-xs text-neutral-500">Isi nama barang, satuan, qty, dan harga. Total dihitung otomatis.</p>
          </div>
          <button
            type="button"
            onClick={onAddManualItem}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-white bg-brand-900 rounded-xl active:hover:scale-90"
          >
            <Plus className="w-4 h-4" /> Tambah Barang
          </button>
        </div>

        {items.length > 0 && (
        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Nama Barang</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Satuan</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Harga</th>
                <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Total</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Catatan</th>
                <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((it) => (
                <tr key={it._uid}>
                  <td className="px-3 py-2 align-top">
                    <input
                      value={it.product_unit_name}
                      onChange={(e) => onUpdateItem(it._uid, { product_unit_name: e.target.value })}
                      placeholder="Nama barang"
                      className="w-48 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                    />
                    {it.product_unit_id && (
                      <div className="text-[10px] text-neutral-500">{it.product_unit_id}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      value={it.uom}
                      onChange={(e) => onUpdateItem(it._uid, { uom: e.target.value })}
                      className="w-20 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="number"
                      min={0}
                      value={it.qty}
                      onChange={(e) => onUpdateItem(it._uid, { qty: e.target.value })}
                      className="w-24 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="number"
                      min={0}
                      placeholder="-"
                      value={it.price}
                      onChange={(e) => onUpdateItem(it._uid, { price: e.target.value })}
                      className="w-28 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                    />
                  </td>
                  <td className="px-3 py-2 text-sm text-right align-top whitespace-nowrap">
                    {num(it.price) > 0 ? formatMoney(num(it.qty) * num(it.price)) : "-"}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      value={it.notes}
                      onChange={(e) => onUpdateItem(it._uid, { notes: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded-lg border-neutral-200"
                    />
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <button
                      type="button"
                      onClick={() => onRemoveItem(it._uid)}
                      className="text-xs font-semibold text-red-600 hover:underline"
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-neutral-50">
              <tr>
                <td className="px-3 py-2 text-xs font-semibold text-right uppercase" colSpan={2}>
                  Total Qty
                </td>
                <td className="px-3 py-2 text-sm font-semibold">
                  {formatQty(totalQty)}
                </td>
                <td className="px-3 py-2 text-xs font-semibold text-right uppercase">
                  Total
                </td>
                <td className="px-3 py-2 text-sm font-semibold text-right whitespace-nowrap">
                  {formatMoney(grandTotal)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate("/surat-jalan-umum")}
          className="px-5 py-2 text-sm font-semibold border rounded-xl border-neutral-300 text-neutral-700"
        >
          Batal
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
          className={`px-5 py-2 text-sm font-semibold text-white rounded-xl ${
            submitting ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
          }`}
        >
          {isEdit ? "Simpan Perubahan" : "Simpan"}
        </button>
      </div>
    </div>
  )
}
